import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Spinner } from 'react-bootstrap';

import api from './api';

const HEARTBEAT_INTERVAL_MS = 30000;
const WARNING_DEFAULT_SECONDS = 120;

const formatCountdown = (totalSeconds) => {
  const safe = Math.max(0, Number.parseInt(String(totalSeconds || 0), 10) || 0);
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

export default function CollaboraWrapper({
  siteCfg = {},
  sessionId: sessionIdProp = '',
  embedded = false,
  onSessionFinished = null,
  showActions = true,
  containerHeight = '100vh',
}) {
  const [searchParams] = useSearchParams();
  const sessionId = useMemo(
    () => String(sessionIdProp || searchParams.get('session_id') || '').trim(),
    [searchParams, sessionIdProp],
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [collaboraUrl, setCollaboraUrl] = useState('');
  const [filename, setFilename] = useState('');
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [warningSeconds, setWarningSeconds] = useState(WARNING_DEFAULT_SECONDS);
  const [isFinishing, setIsFinishing] = useState(false);

  const heartbeatRef = useRef(null);
  const countdownRef = useRef(null);
  const autoFinishRef = useRef(false);
  const collaboraFrameRef = useRef(null);
  const saveAckWaiterRef = useRef(null);

  const isWarning = remainingSeconds > 0 && remainingSeconds <= warningSeconds;
  const isExpired = remainingSeconds <= 0;

  const invalidSessionLabel =
    siteCfg?.texts?.other?.dashboard_generated_document_collabora_invalid_session ||
    'Session Collabora invalide.';
  const loadErrorLabel =
    siteCfg?.texts?.other?.dashboard_generated_document_collabora_load_error ||
    'Impossible de charger la session Collabora.';
  const finishErrorLabel =
    siteCfg?.texts?.other?.dashboard_generated_document_collabora_finish_error ||
    "Impossible de finaliser l'edition.";
  const wrapperTitleLabel =
    siteCfg?.texts?.other?.dashboard_generated_document_collabora_wrapper_title ||
    'Edition Collabora';
  const sessionBadgeLabel =
    siteCfg?.texts?.other?.dashboard_generated_document_collabora_session_label ||
    'Session';
  const readOnlyBadgeLabel =
    siteCfg?.texts?.other?.dashboard_generated_document_collabora_read_only_label ||
    'Lecture seule';
  const cancelButtonLabel =
    siteCfg?.texts?.other?.dashboard_generated_document_collabora_cancel_button ||
    'Annuler et revenir';
  const saveButtonLabel =
    siteCfg?.texts?.other?.dashboard_generated_document_collabora_save_button ||
    'Enregistrer et revenir';
  const savingButtonLabel =
    siteCfg?.texts?.other?.dashboard_generated_document_collabora_saving_button ||
    'Enregistrement...';
  const warningLabelTemplate =
    siteCfg?.texts?.other?.dashboard_generated_document_collabora_warning ||
    'Votre session expire bientot ({countdown}).';
  const expiredLabel =
    siteCfg?.texts?.other?.dashboard_generated_document_collabora_expired ||
    'Session expiree. Les modifications ont ete gardees en brouillon.';
  const unavailableLabel =
    siteCfg?.texts?.other?.dashboard_generated_document_collabora_unavailable ||
    'Edition indisponible.';
  const collaboraOrigin = useMemo(() => {
    try {
      return new URL(String(collaboraUrl || '')).origin;
    } catch {
      return '';
    }
  }, [collaboraUrl]);

  const stopIntervals = () => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  };

  const notifySessionFinished = useCallback((payload) => {
    if (typeof onSessionFinished === 'function') {
      onSessionFinished(payload);
      return;
    }
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage(
        {
          type: 'COLLABORA_SESSION_FINISHED',
          ...payload,
        },
        window.location.origin,
      );
    }
  }, [onSessionFinished]);

  const loadSession = async () => {
    if (!sessionId) {
      setError(invalidSessionLabel);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await api.get(`document-templates/collabora/session/${sessionId}/`);
      setCollaboraUrl(String(response?.data?.collabora_url || ''));
      setFilename(String(response?.data?.filename || ''));
      setIsReadOnly(Boolean(response?.data?.read_only));
      setRemainingSeconds(Number.parseInt(String(response?.data?.remaining_seconds || 0), 10) || 0);
      setWarningSeconds(Number.parseInt(String(response?.data?.warning_seconds || WARNING_DEFAULT_SECONDS), 10) || WARNING_DEFAULT_SECONDS);
      autoFinishRef.current = false;
    } catch {
      setError(loadErrorLabel);
    } finally {
      setLoading(false);
    }
  };

  const sendHeartbeat = async () => {
    if (!sessionId || isFinishing) {
      return;
    }
    try {
      const response = await api.post(
        `document-templates/collabora/session/${sessionId}/heartbeat/`,
      );
      setRemainingSeconds(
        Number.parseInt(String(response?.data?.remaining_seconds || 0), 10) || 0,
      );
      setWarningSeconds(
        Number.parseInt(
          String(response?.data?.warning_seconds || WARNING_DEFAULT_SECONDS),
          10,
        ) || WARNING_DEFAULT_SECONDS,
      );
    } catch {
      setRemainingSeconds(0);
    }
  };

  const postToCollaboraFrame = useCallback((message) => {
    const frameWindow = collaboraFrameRef.current?.contentWindow;
    if (!frameWindow) {
      return false;
    }

    const targetOrigin = collaboraOrigin || '*';
    try {
      frameWindow.postMessage(JSON.stringify(message), targetOrigin);
    } catch {
      // Best-effort: some integrations accept object payloads.
    }
    try {
      frameWindow.postMessage(message, targetOrigin);
    } catch {
      // Ignore, JSON message path above is preferred.
    }
    return true;
  }, [collaboraOrigin]);

  const notifyCollaboraHostReady = useCallback(() => {
    postToCollaboraFrame({
      MessageId: 'Host_PostmessageReady',
      SendTime: Date.now(),
      Values: {},
    });
  }, [postToCollaboraFrame]);

  const requestCollaboraSave = useCallback(async () => {
    const hasFrame = Boolean(collaboraFrameRef.current?.contentWindow);
    if (!hasFrame) {
      return false;
    }

    notifyCollaboraHostReady();
    await new Promise((resolve) => window.setTimeout(resolve, 50));

    const saveAckPromise = new Promise((resolve) => {
      const timeoutId = window.setTimeout(() => {
        const waiter = saveAckWaiterRef.current;
        if (waiter) {
          saveAckWaiterRef.current = null;
          resolve(false);
        }
      }, 9000);

      saveAckWaiterRef.current = {
        resolve: (success) => {
          window.clearTimeout(timeoutId);
          saveAckWaiterRef.current = null;
          resolve(Boolean(success));
        },
      };
    });

    const posted = postToCollaboraFrame({
      MessageId: 'Action_Save',
      SendTime: Date.now(),
      Values: {
        Notify: true,
        DontTerminateEdit: true,
        DontSaveIfUnmodified: false,
      },
    });
    if (!posted) {
      const waiter = saveAckWaiterRef.current;
      if (waiter) {
        waiter.resolve(false);
      }
      return false;
    }

    return saveAckPromise;
  }, [notifyCollaboraHostReady, postToCollaboraFrame]);

  const finishSession = async (action) => {
    if (!sessionId || isFinishing) {
      return;
    }
    setIsFinishing(true);
    try {
      if (action === 'save' && !isReadOnly) {
        const saveConfirmed = await requestCollaboraSave();
        if (!saveConfirmed) {
          setError(finishErrorLabel);
          return;
        }
      }
      const response = await api.post(
        `document-templates/collabora/session/${sessionId}/finish/`,
        { action },
      );
      notifySessionFinished({
        session_id: sessionId,
        action,
        status: String(response?.data?.status || action),
        saved: Boolean(response?.data?.saved),
        name: String(response?.data?.name || ''),
        document_type: String(response?.data?.document_type || ''),
        draft_exists: Boolean(response?.data?.draft_exists),
      });
      if (!embedded) {
        window.close();
      }
    } catch {
      setError(finishErrorLabel);
    } finally {
      setIsFinishing(false);
    }
  };

  useEffect(() => {
    loadSession();
    return () => {
      stopIntervals();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    stopIntervals();
    if (!sessionId || loading || error || isExpired) {
      return undefined;
    }

    heartbeatRef.current = setInterval(() => {
      void sendHeartbeat();
    }, HEARTBEAT_INTERVAL_MS);

    countdownRef.current = setInterval(() => {
      setRemainingSeconds((previous) => Math.max(0, previous - 1));
    }, 1000);

    return () => {
      stopIntervals();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, loading, error, isExpired, isFinishing]);

  useEffect(() => {
    if (loading || error || !isExpired || isFinishing || autoFinishRef.current) {
      return;
    }
    autoFinishRef.current = true;
    void finishSession('cancel');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error, isExpired, isFinishing]);

  const title = useMemo(() => {
    if (!filename) {
      return wrapperTitleLabel;
    }
    return `${wrapperTitleLabel} - ${filename}`;
  }, [filename, wrapperTitleLabel]);

  useEffect(() => {
    const handleCollaboraMessage = (event) => {
      if (collaboraOrigin && event.origin !== collaboraOrigin) {
        return;
      }

      let payload = null;
      if (typeof event.data === 'string') {
        try {
          payload = JSON.parse(event.data);
        } catch {
          payload = null;
        }
      } else if (event.data && typeof event.data === 'object') {
        payload = event.data;
      }
      if (!payload || typeof payload !== 'object') {
        return;
      }

      if (String(payload.MessageId || '') === 'Action_Save_Resp') {
        const waiter = saveAckWaiterRef.current;
        if (!waiter || typeof waiter.resolve !== 'function') {
          return;
        }
        const success = payload?.Values?.success !== false;
        waiter.resolve(success);
      }
    };

    window.addEventListener('message', handleCollaboraMessage);
    return () => {
      window.removeEventListener('message', handleCollaboraMessage);
    };
  }, [collaboraOrigin]);

  if (loading) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ minHeight: containerHeight }}
      >
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="d-flex flex-column" style={{ height: containerHeight }}>
      <div className="border-bottom bg-body px-3 py-2 d-flex align-items-center gap-3 flex-wrap">
        <strong>{title}</strong>
        <span className={`badge ${isWarning ? 'bg-warning text-dark' : 'bg-secondary'}`}>
          {sessionBadgeLabel}: {formatCountdown(remainingSeconds)}
        </span>
        {isReadOnly ? <span className="badge bg-info text-dark">{readOnlyBadgeLabel}</span> : null}
        {showActions ? (
          <div className="ms-auto d-flex align-items-center gap-2">
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => finishSession('discard')}
              disabled={isFinishing}
            >
              {cancelButtonLabel}
            </Button>
            {!isReadOnly ? (
              <Button
                size="sm"
                variant="primary"
                onClick={() => finishSession('save')}
                disabled={isFinishing || isExpired}
              >
                {isFinishing ? savingButtonLabel : saveButtonLabel}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      {error ? <div className="alert alert-danger m-3 mb-0">{error}</div> : null}
      {isWarning && !isExpired ? (
        <div className="alert alert-warning m-3 mb-0">
          {warningLabelTemplate.replace(
            '{countdown}',
            formatCountdown(remainingSeconds),
          )}
        </div>
      ) : null}
      {isExpired ? (
        <div className="alert alert-danger m-3 mb-0">
          {expiredLabel}
        </div>
      ) : null}

      <div className="flex-grow-1">
        {!isExpired && collaboraUrl ? (
          <iframe
            ref={collaboraFrameRef}
            title="Collabora Online"
            src={collaboraUrl}
            onLoad={notifyCollaboraHostReady}
            style={{ width: '100%', height: '100%', border: 0 }}
            allow="clipboard-read; clipboard-write"
          />
        ) : (
          <div className="h-100 d-flex align-items-center justify-content-center text-muted">
            {unavailableLabel}
          </div>
        )}
      </div>
    </div>
  );
}
